import React, { useCallback } from "react";

import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useIntl } from "react-intl";

import { EntityTypeSupportingPreview } from "@mapmycustomers/shared/types/entity";
import { EntityTypeShapedIcon } from "@mapmycustomers/ui";

import { InternalFieldDescription } from "@app/store/recordPreview/EntityColumnsConfiguration";
import { getEntityTypeDisplayName } from "@app/util/ui";

import useFieldAccess from "../util/useFieldAccess";

import styles from "./EntityFields.module.scss";
import FieldItem from "./FieldItem";

interface Props {
  entityType: EntityTypeSupportingPreview;
  fields: InternalFieldDescription[];
  onChange: (
    fields: InternalFieldDescription[],
    entityType: EntityTypeSupportingPreview,
    dataChanged?: boolean
  ) => void;
}

const EntityFields: React.FC<Props> = ({ entityType, fields, onChange }) => {
  const intl = useIntl();

  const [disabledFields, allowedFields, startDisplayOrder] = useFieldAccess(fields);

  const handleDragEnd = useCallback(
    ({ destination, source }) => {
      if (!destination) {
        return;
      }

      const result = [...allowedFields];
      const startIndex = source.index;
      const endIndex = destination?.index;

      if (startIndex === endIndex) {
        return;
      }

      const [item] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, item);
      const updatedFields = result.map((item, index) => ({
        ...item,
        displayOrder: startDisplayOrder + index,
      }));

      onChange([...disabledFields, ...updatedFields], entityType);
    },
    [allowedFields, onChange, disabledFields, entityType, startDisplayOrder]
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className={styles.container}>
        <div className={styles.title}>
          <EntityTypeShapedIcon className={styles.entityIcon} entityType={entityType} />
          {getEntityTypeDisplayName(intl, entityType, { lowercase: false })}
        </div>
        <div className={styles.allFields}>
          <div className={styles.fields}>
            {disabledFields.map((fieldDescription) => {
              return (
                <FieldItem
                  disabled
                  entityType={entityType}
                  fieldDescription={fieldDescription}
                  fields={fields}
                  key={fieldDescription.field.name}
                  onChange={onChange}
                />
              );
            })}
          </div>
          <Droppable droppableId={entityType} key={entityType}>
            {(provided) => (
              <div className={styles.fields} {...provided.droppableProps} ref={provided.innerRef}>
                {allowedFields.map((fieldDescription, index) => {
                  return (
                    <Draggable
                      draggableId={fieldDescription.field.name}
                      index={index}
                      key={fieldDescription.field.name}
                    >
                      {(provided) => (
                        <FieldItem
                          entityType={entityType}
                          fieldDescription={fieldDescription}
                          fields={fields}
                          onChange={onChange}
                          provided={provided}
                        />
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </div>
    </DragDropContext>
  );
};
export default EntityFields;
